import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Play, Search, Clock, Crown, Tv, Film, Music, 
  Mic2, PartyPopper, Disc3, Sparkles, Lock,
  User, LogOut, Menu, X, Settings, MoreVertical,
  TrendingUp, History, Bookmark, Video, Radio, ChevronLeft, ChevronRight,
  Users, BadgeCheck, Home, Compass, Bell, Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PromoPopup from '../components/PromoPopup';
import ReelsSection from '../components/ReelsSection';
import VODFooter from '../components/VODFooter';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Default site title - can be overridden by SEO settings
const DEFAULT_SITE_TITLE = 'LTV Production - Stream & Watch Videos';

// Helper to detect if user is on ltvvod.com forwarding domain
const isOnLtvvodDomain = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname.includes('ltvvod.com');
  }
  return false;
};

// Get login URL with proper redirect
const getLoginUrl = (redirectPath) => {
  const fromParam = isOnLtvvodDomain() ? 'from=ltvvod&' : 'from=vod&';
  return `/live/login?${fromParam}redirect=${redirectPath}`;
};

// Format view count
const formatViews = (views) => {
  if (!views) return '0 views';
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M views`;
  if (views >= 1000) return `${(views / 1000).toFixed(1)}K views`;
  return `${views} views`;
};

// Format relative time
const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`;
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} months ago`;
  return `${Math.floor(seconds / 31536000)} years ago`;
};

// Format duration from seconds to mm:ss or hh:mm:ss
const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds) || seconds < 0) return '';
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const VODLanding = () => {
  const navigate = useNavigate();
  const categoryScrollRef = useRef(null);
  
  // State
  const [categories, setCategories] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [creators, setCreators] = useState([]);
  const [trending, setTrending] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [homepageLayout, setHomepageLayout] = useState([]);
  const [seoSettings, setSeoSettings] = useState(null);
  const [isPremiumMember, setIsPremiumMember] = useState(false);
  
  // UI State
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [blogArticles, setBlogArticles] = useState([]);

  // Helper to check if a section is visible based on homepage layout settings
  const isSectionVisible = (sectionId) => {
    if (homepageLayout.length === 0) return true; // Default to visible if no layout loaded
    const section = homepageLayout.find(s => s.id === sectionId || s.name === sectionId);
    return section ? section.is_visible : true;
  };

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    if (token) {
      const customerData = localStorage.getItem('livestreamCustomer');
      if (customerData) {
        const parsedCustomer = JSON.parse(customerData);
        setCustomer(parsedCustomer);
        if (parsedCustomer.is_admin === true || parsedCustomer.is_staff === true) {
          setIsAdmin(true);
        }
        // Check local storage for quick premium detection
        if (parsedCustomer.is_premium || parsedCustomer.membership_tier === 'premium' || parsedCustomer.has_active_subscription) {
          setIsPremiumMember(true);
        }
      }
      // Fetch accurate subscription status from API
      fetchSubscriptionStatus(token);
    }
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      setIsAdmin(true);
    }
    fetchData();
  }, []);

  // Fetch latest blog articles from API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/api/blog/articles?published_only=true`);
        if (res.data && res.data.length > 0) {
          setBlogArticles(res.data);
        }
      } catch (err) {
        console.log('Failed to fetch blog articles');
      }
    };
    fetchArticles();
  }, []);


  // Fetch subscription status to determine premium membership
  const fetchSubscriptionStatus = async (token) => {
    try {
      const res = await axios.get(`${API}/api/vod/membership/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.subscription && res.data.subscription.tier === 'premium' && res.data.subscription.status === 'active') {
        setIsPremiumMember(true);
        // Update customer state to reflect premium status
        setCustomer(prev => prev ? { ...prev, is_premium: true, has_active_subscription: true } : prev);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const requests = [
        axios.get(`${API}/api/vod/categories`).catch(() => ({ data: { categories: [] } })),
        axios.get(`${API}/api/vod/videos?limit=50`, { headers }).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/api/creators/profiles?limit=20`).catch(() => ({ data: { profiles: [] } })),
        axios.get(`${API}/api/vod/trending`).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/api/homepage/layout`).catch(() => ({ data: { sections: [] } })),
        axios.get(`${API}/api/seo/vod/settings`).catch(() => ({ data: {} })),
      ];

      if (token) {
        requests.push(
          axios.get(`${API}/api/vod/continue-watching`, { headers }).catch(() => ({ data: { videos: [] } }))
        );
      }

      const results = await Promise.all(requests);

      const cats = results[0].data.categories || [];
      setCategories(cats);
      
      const videos = results[1].data.videos || [];
      setAllVideos(videos);
      setFilteredVideos(videos);
      
      setCreators(results[2].data.profiles || []);
      setTrending(results[3].data.videos || []);
      
      // Set homepage layout - sort by order
      const layoutSections = results[4].data.sections || [];
      setHomepageLayout(layoutSections.sort((a, b) => a.order - b.order));
      
      // Set SEO settings
      setSeoSettings(results[5].data || {});
      
      if (token && results[6]) {
        setContinueWatching(results[6].data.videos || []);
      }
    } catch (err) {
      console.error('Error fetching VOD data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter videos by category
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredVideos(allVideos);
    } else if (selectedCategory === 'trending') {
      setFilteredVideos(trending.length > 0 ? trending : allVideos.slice(0, 10));
    } else if (selectedCategory === 'creators') {
      // Show videos that have a creator_id (videos from creators)
      const creatorVideos = allVideos.filter(v => v.creator_id);
      setFilteredVideos(creatorVideos.length > 0 ? creatorVideos : allVideos);
    } else {
      const filtered = allVideos.filter(v => v.category_id === selectedCategory);
      setFilteredVideos(filtered.length > 0 ? filtered : allVideos);
    }
  }, [selectedCategory, allVideos, trending]);

  const handleSignOut = () => {
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('staffPermissions');
    localStorage.removeItem('isStaff');
    setCustomer(null);
    setIsAdmin(false);
    setMobileMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/vod/browse?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Category scroll handlers
  const updateScrollButtons = () => {
    if (categoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      categoryScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(updateScrollButtons, 300);
    }
  };

  const getCategoryIcon = (name) => {
    const icons = {
      'series': <Tv className="h-4 w-4" />,
      'shows': <Tv className="h-4 w-4" />,
      'podcasts': <Mic2 className="h-4 w-4" />,
      'music': <Music className="h-4 w-4" />,
      'entertainment': <PartyPopper className="h-4 w-4" />,
      'film': <Film className="h-4 w-4" />,
      'movies': <Film className="h-4 w-4" />,
      'live': <Radio className="h-4 w-4" />,
    };
    return icons[name?.toLowerCase()] || <Video className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white flex flex-col">
      <Helmet>
        <title>{seoSettings?.site_title || DEFAULT_SITE_TITLE}</title>
        <meta name="description" content={seoSettings?.meta_description || "Stream and watch exclusive videos on LTV Production"} />
        {seoSettings?.og_title && <meta property="og:title" content={seoSettings.og_title} />}
        {seoSettings?.og_description && <meta property="og:description" content={seoSettings.og_description} />}
        {seoSettings?.og_image && <meta property="og:image" content={seoSettings.og_image} />}
        {seoSettings?.twitter_title && <meta name="twitter:title" content={seoSettings.twitter_title} />}
        {seoSettings?.twitter_description && <meta name="twitter:description" content={seoSettings.twitter_description} />}
        {seoSettings?.twitter_image && <meta name="twitter:image" content={seoSettings.twitter_image} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Promo Popup */}
      <PromoPopup />

      {/* ===== HEADER - Compact YouTube Style ===== */}
      <header className="sticky top-0 z-50 bg-[#0f0f0f]/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14">
          {/* Left - Logo & Menu */}
          <div className="flex items-center gap-2 sm:gap-4">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-white/10 rounded-full lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div 
              onClick={() => navigate('/vod')} 
              className="flex items-center gap-2 cursor-pointer"
            >
              <img 
                src="/ltv-vod-logo.png" 
                alt="LTV VOD" 
                className="h-10 sm:h-12 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <nav className="hidden lg:flex items-center gap-3 ml-2">
              <a href="/about" className="text-xs text-gray-500 hover:text-white transition-colors" data-testid="vod-nav-about">About</a>
              <a href="/contact" className="text-xs text-gray-500 hover:text-white transition-colors" data-testid="vod-nav-contact">Contact</a>
              <a href="/privacy" className="text-xs text-gray-500 hover:text-white transition-colors" data-testid="vod-nav-privacy">Privacy</a>
              <a href="/terms" className="text-xs text-gray-500 hover:text-white transition-colors" data-testid="vod-nav-terms">Terms</a>
            </nav>
          </div>

          {/* Center - Search (Desktop) */}
          <div className="hidden md:flex flex-1 max-w-xl mx-4">
            <form onSubmit={handleSearch} className="w-full flex">
              <Input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-[#121212] border-gray-700 rounded-l-full rounded-r-none focus:border-blue-500 pl-4"
              />
              <button 
                type="submit"
                className="px-5 bg-[#222222] border border-l-0 border-gray-700 rounded-r-full hover:bg-[#333333] transition"
              >
                <Search className="h-5 w-5 text-gray-400" />
              </button>
            </form>
          </div>

          {/* Right - Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Mobile Search Toggle */}
            <button 
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 hover:bg-white/10 rounded-full md:hidden"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Creator Package Button - Hidden on mobile */}
            <button 
              onClick={() => navigate('/creator')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gradient-to-r from-purple-600/20 to-pink-600/20 text-purple-300 rounded-full hover:from-purple-600/30 hover:to-pink-600/30 transition border border-purple-500/30"
            >
              <Video className="h-4 w-4" />
              <span className="hidden lg:inline">Creator Package</span>
            </button>

            {/* Go Premium Button - Hidden on mobile */}
            <button 
              onClick={() => navigate('/vod/membership')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 rounded-full hover:from-yellow-500/30 hover:to-orange-500/30 transition border border-yellow-500/30"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden lg:inline">Go Premium</span>
            </button>

            {customer ? (
              <>
                {isAdmin && (
                  <button 
                    onClick={() => navigate('/vod/admin')}
                    className="hidden sm:flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600/20 text-purple-400 rounded-full hover:bg-purple-600/30 transition"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="hidden lg:inline">Admin</span>
                  </button>
                )}
                <button className="p-2 hover:bg-white/10 rounded-full hidden sm:block">
                  <Bell className="h-5 w-5" />
                </button>
                <div className="relative group">
                  <button className="flex items-center gap-2 p-1 hover:bg-white/10 rounded-full">
                    <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold">
                      {customer.full_name?.charAt(0) || customer.email?.charAt(0) || 'U'}
                    </div>
                  </button>
                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-48 bg-[#282828] rounded-xl shadow-xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                    <div className="px-4 py-2 border-b border-white/10">
                      <p className="font-medium text-sm truncate">{customer.full_name || 'User'}</p>
                      <p className="text-xs text-gray-400 truncate">{customer.email}</p>
                    </div>
                    <button 
                      onClick={() => navigate('/vod/account')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center gap-2"
                    >
                      <User className="h-4 w-4" /> My Account
                    </button>
                    <button 
                      onClick={() => navigate('/vod/browse?view=watchlist')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center gap-2"
                    >
                      <Bookmark className="h-4 w-4" /> Watchlist
                    </button>
                    {isAdmin && (
                      <button 
                        onClick={() => navigate('/vod/admin')}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center gap-2 sm:hidden"
                      >
                        <Settings className="h-4 w-4" /> Admin
                      </button>
                    )}
                    <button 
                      onClick={handleSignOut}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 flex items-center gap-2 text-red-400"
                    >
                      <LogOut className="h-4 w-4" /> Sign Out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <Button 
                onClick={() => navigate(getLoginUrl('/vod'))}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-1.5 text-sm font-medium"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Search Bar (Expandable) */}
        {searchOpen && (
          <div className="md:hidden px-3 pb-3">
            <form onSubmit={handleSearch} className="flex">
              <Input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-[#121212] border-gray-700 rounded-l-full pl-4"
                autoFocus
              />
              <button 
                type="submit"
                className="px-4 bg-[#222222] border border-l-0 border-gray-700 rounded-r-full"
              >
                <Search className="h-5 w-5" />
              </button>
            </form>
          </div>
        )}
      </header>

      {/* ===== CATEGORY PILLS - YouTube Style ===== */}
      <div className="sticky top-14 z-40 bg-[#0f0f0f] border-b border-white/5">
        <div className="relative flex items-center">
          {/* Left scroll button */}
          {canScrollLeft && (
            <button 
              onClick={() => scrollCategories('left')}
              className="absolute left-0 z-10 h-full px-2 bg-gradient-to-r from-[#0f0f0f] via-[#0f0f0f] to-transparent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          
          {/* Scrollable pills */}
          <div 
            ref={categoryScrollRef}
            onScroll={updateScrollButtons}
            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <button
              onClick={() => setSelectedCategory('all')}
              className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                selectedCategory === 'all' 
                  ? 'bg-white text-black' 
                  : 'bg-[#272727] text-white hover:bg-[#3f3f3f]'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedCategory('trending')}
              className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                selectedCategory === 'trending' 
                  ? 'bg-white text-black' 
                  : 'bg-[#272727] text-white hover:bg-[#3f3f3f]'
              }`}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              Trending
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  selectedCategory === cat.id 
                    ? 'bg-white text-black' 
                    : 'bg-[#272727] text-white hover:bg-[#3f3f3f]'
                }`}
              >
                {getCategoryIcon(cat.name)}
                {cat.name}
              </button>
            ))}
            {creators.length > 0 && (
              <button
                onClick={() => setSelectedCategory('creators')}
                className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  selectedCategory === 'creators' 
                    ? 'bg-white text-black' 
                    : 'bg-[#272727] text-white hover:bg-[#3f3f3f]'
                }`}
              >
                <Users className="h-3.5 w-3.5" />
                Creators
              </button>
            )}
          </div>
          
          {/* Right scroll button */}
          {canScrollRight && (
            <button 
              onClick={() => scrollCategories('right')}
              className="absolute right-0 z-10 h-full px-2 bg-gradient-to-l from-[#0f0f0f] via-[#0f0f0f] to-transparent"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* ===== MAIN CONTENT - Video Grid ===== */}
      <main className="flex-1 px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Premium Member Banner - Show for active premium subscribers */}
        {customer && isPremiumMember && (
          <section className="mb-6" data-testid="premium-member-banner">
            <div className="bg-gradient-to-r from-purple-900/60 via-pink-900/40 to-purple-900/60 rounded-2xl p-4 sm:p-6 border border-purple-500/30 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                    Premium Member
                    <Badge className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-yellow-300 text-xs">
                      ACTIVE
                    </Badge>
                  </h2>
                  <p className="text-gray-300 text-sm">
                    Enjoy ad-free streaming and exclusive premium content
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/vod/account')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6"
              >
                Manage Subscription
              </Button>
            </div>
          </section>
        )}

        {/* Hero Section - VOD Platform Introduction */}
        {!customer && (
          <section className="mb-8 bg-gradient-to-r from-purple-900/40 via-purple-800/30 to-pink-900/40 rounded-2xl p-6 sm:p-8 border border-purple-500/20">
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                  Stream Exclusive Content on LTV VOD
                </h1>
                <p className="text-gray-300 mb-4 text-sm sm:text-base">
                  Your premium destination for on-demand video streaming. Watch shows, podcasts, concerts, and exclusive creator content anytime, anywhere.
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
                  <div className="flex items-center gap-2">
                    <Play className="h-4 w-4 text-purple-400" />
                    <span>Unlimited streaming</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-yellow-400" />
                    <span>Premium exclusives</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-400" />
                    <span>Creator channels</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/vod/membership')}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-full font-semibold transition-all"
                  >
                    View Plans & Pricing
                  </button>
                  <button
                    onClick={() => setShowAuth(true)}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full font-semibold transition-all"
                  >
                    Sign Up Free
                  </button>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="grid grid-cols-2 gap-3 opacity-80">
                  <div className="w-24 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                    <Play className="h-8 w-8" />
                  </div>
                  <div className="w-24 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                    <Tv className="h-8 w-8" />
                  </div>
                  <div className="w-24 h-16 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg flex items-center justify-center">
                    <Mic2 className="h-8 w-8" />
                  </div>
                  <div className="w-24 h-16 bg-gradient-to-br from-green-600 to-teal-600 rounded-lg flex items-center justify-center">
                    <Music className="h-8 w-8" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Continue Watching Section */}
        {continueWatching.length > 0 && isSectionVisible('continue_watching') && (
          <section className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-bold mb-3 flex items-center gap-2">
              <History className="h-5 w-5 text-blue-400" />
              Continue Watching
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {continueWatching.slice(0, 4).map(video => (
                <VideoCard key={video.id} video={video} navigate={navigate} creators={creators} />
              ))}
            </div>
          </section>
        )}

        {/* Main Video Grid - FIRST (or Creator Profiles if Creators tab selected) */}
        <section className="mb-8">
          {selectedCategory !== 'all' && (
            <h2 className="text-lg sm:text-xl font-bold mb-3 capitalize">
              {selectedCategory === 'trending' ? 'Trending Now' : 
               selectedCategory === 'creators' ? 'Our Creators' :
               categories.find(c => c.id === selectedCategory)?.name || 'Videos'}
            </h2>
          )}
          
          {/* Show Creator Profiles when Creators tab is selected */}
          {selectedCategory === 'creators' ? (
            creators.length === 0 ? (
              <div className="text-center py-16">
                <Users className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No creators found</h3>
                <p className="text-gray-400">Check back later for new creators</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-6 sm:gap-y-8">
                {creators.map(creator => (
                  <CreatorProfileCard key={creator.id} creator={creator} navigate={navigate} />
                ))}
              </div>
            )
          ) : (
            /* Show Videos for other categories */
            filteredVideos.length === 0 ? (
              <div className="text-center py-16">
                <Video className="h-16 w-16 mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No videos found</h3>
                <p className="text-gray-400">Check back later for new content</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-6 sm:gap-y-8">
                {filteredVideos.map(video => (
                  <VideoCard key={video.id} video={video} navigate={navigate} creators={creators} />
                ))}
              </div>
            )
          )}
        </section>

        {/* Reels Section - SECOND (after videos) */}
        {selectedCategory === 'all' && isSectionVisible('reels') && (
          <ReelsSection />
        )}

        {/* Our Creators Section */}
        {creators.length > 0 && selectedCategory === 'all' && isSectionVisible('our_creators') && (
          <section className="mt-10 sm:mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-400" />
                Our Creators
              </h2>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/vod/browse?view=creators')} 
                className="text-purple-400 text-sm"
              >
                View All <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide -mx-3 px-3 sm:mx-0 sm:px-0">
              {creators.slice(0, 8).map(creator => (
                <CreatorCard key={creator.id} creator={creator} navigate={navigate} />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#212121] overflow-y-auto">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <img src="/ltv-vod-logo.png" alt="LTV VOD" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
              <button onClick={() => setMobileMenuOpen(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="p-2">
              <button onClick={() => { navigate('/vod'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10">
                <Home className="h-5 w-5" /> Home
              </button>
              <button onClick={() => { navigate('/vod/browse'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10">
                <Compass className="h-5 w-5" /> Browse
              </button>
              <button onClick={() => { navigate('/vod/browse?view=creators'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10">
                <Users className="h-5 w-5" /> Creators
              </button>
              {customer && (
                <>
                  <div className="border-t border-white/10 my-2" />
                  <button onClick={() => { navigate('/vod/browse?view=watchlist'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10">
                    <Bookmark className="h-5 w-5" /> Watchlist
                  </button>
                  <button onClick={() => { navigate('/vod/account'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10">
                    <User className="h-5 w-5" /> My Account
                  </button>
                </>
              )}
              {isAdmin && (
                <>
                  <div className="border-t border-white/10 my-2" />
                  <button onClick={() => { navigate('/vod/admin'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-purple-400">
                    <Settings className="h-5 w-5" /> Admin Dashboard
                  </button>
                </>
              )}
              <div className="border-t border-white/10 my-2" />
              <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider">Info</p>
              <button onClick={() => { navigate('/about'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-gray-400" data-testid="mobile-nav-about">
                About Us
              </button>
              <button onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-gray-400" data-testid="mobile-nav-contact">
                Contact
              </button>
              <button onClick={() => { navigate('/privacy'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-gray-400" data-testid="mobile-nav-privacy">
                Privacy Policy
              </button>
              <button onClick={() => { navigate('/terms'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-gray-400" data-testid="mobile-nav-terms">
                Terms of Service
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Blog Section */}
      {blogArticles.length > 0 && (
      <section className="py-12 bg-gradient-to-b from-transparent to-gray-900/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">Latest Articles</h2>
              <p className="text-gray-400 mt-1">Insights, guides & stories from the world of streaming</p>
            </div>
            <Link 
              to="/vod/blog" 
              className="hidden sm:flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
            >
              View All
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogArticles.slice(0, 4).map(article => (
              <Link 
                key={article.id}
                to={`/vod/blog/${article.slug}`}
                className="group bg-gray-900/50 rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all hover:transform hover:scale-[1.02]"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800'; }}
                  />
                </div>
                <div className="p-4">
                  <span className="text-purple-400 text-xs font-medium">{article.category}</span>
                  <h3 className="text-base font-semibold mt-1 mb-2 group-hover:text-purple-400 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {article.readTime || article.read_time}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="mt-8 text-center sm:hidden">
            <Link 
              to="/vod/blog" 
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              View All Articles
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
      )}
      
      {/* About & FAQ Content Section - Rich text for SEO */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* About */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">About LTV VOD</h2>
              <div className="space-y-3 text-gray-400 leading-relaxed text-sm">
                <p>
                  LTV VOD is a premium video-on-demand streaming platform created by LTV Production LTD, a UK-based media and entertainment company. We specialise in bringing exclusive, culturally rich content to audiences worldwide, with a focus on Caribbean culture, independent film, live event coverage, music, and creator-led entertainment.
                </p>
                <p>
                  Our platform was built with a dual mission: to provide viewers with high-quality entertainment they cannot find anywhere else, and to give content creators the tools, audience, and fair revenue sharing they need to build sustainable creative careers. We believe that great content comes from empowered creators, and we are committed to building a platform that honours their work.
                </p>
                <p>
                  Whether you are here to discover new shows, rewatch your favourite live performances, or explore behind-the-scenes content from the creators you love, LTV VOD provides a seamless, ad-free viewing experience across all your devices. From original series and documentaries to live-streamed events and exclusive premieres, our growing content library has something for every taste.
                </p>
              </div>
            </div>
            
            {/* FAQ */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {[
                  {
                    q: "What is LTV VOD and how does it work?",
                    a: "LTV VOD is a video-on-demand streaming platform where you can watch exclusive shows, live events, documentaries, and creator content. Simply browse our library, choose what interests you, and start watching instantly on any device. We offer both free content and premium subscriptions for exclusive access."
                  },
                  {
                    q: "What kind of content is available on LTV VOD?",
                    a: "Our content library spans multiple genres including Caribbean cultural programming, independent films, live music events, carnival coverage, behind-the-scenes documentaries, creator vlogs, and original series. We regularly add new content from our growing network of talented creators."
                  },
                  {
                    q: "How can I become a creator on LTV VOD?",
                    a: "We welcome talented creators who produce high-quality, original content. Visit our Creator Package page to learn about partnership opportunities, revenue sharing, production support, and how to apply. We work with creators at every stage of their journey, from emerging talent to established professionals."
                  },
                  {
                    q: "Is LTV VOD available on all devices?",
                    a: "Yes, LTV VOD is accessible through any modern web browser on desktop computers, laptops, tablets, and smartphones. Our responsive platform automatically adapts to your screen size, ensuring the best possible viewing experience regardless of which device you are using."
                  },
                  {
                    q: "How does LTV VOD support its creators?",
                    a: "We offer some of the most competitive revenue sharing in the industry, along with production support, marketing exposure, analytics tools, and community-building features. Our goal is to help every creator on our platform build a sustainable career doing what they love."
                  },
                ].map((faq, i) => (
                  <details key={i} className="group border border-white/10 rounded-lg">
                    <summary className="flex items-center justify-between cursor-pointer px-4 py-3 text-sm font-medium text-white hover:bg-white/5 rounded-lg">
                      {faq.q}
                      <ChevronRight className="h-4 w-4 text-gray-500 group-open:rotate-90 transition-transform" />
                    </summary>
                    <p className="px-4 pb-3 text-sm text-gray-400 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

// ===== VIDEO CARD COMPONENT - YouTube Style =====
const VideoCard = ({ video, navigate, creators }) => {
  const creator = creators?.find(c => c.id === video.creator_id);
  
  return (
    <div 
      className="group cursor-pointer"
      onClick={() => navigate(`/vod/video/${video.slug || video.id}?autoplay=true`)}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video rounded-xl overflow-hidden bg-[#1a1a1a] mb-3">
        {video.thumbnail_url ? (
          <img 
            src={video.thumbnail_url} 
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play className="h-12 w-12 text-gray-600" />
          </div>
        )}
        
        {/* Duration Badge */}
        {video.duration && (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-medium px-1.5 py-0.5 rounded">
            {formatDuration(video.duration)}
          </span>
        )}
        
        {/* Premium Badge */}
        {video.tier === 'premium' && (
          <span className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1">
            <Crown className="h-3 w-3" /> PREMIUM
          </span>
        )}

        {/* Hover Play Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="w-12 h-12 rounded-full bg-black/70 flex items-center justify-center">
            <Play className="h-5 w-5 text-white fill-white ml-0.5" />
          </div>
        </div>
        
        {/* 3-dot menu */}
        <button 
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); }}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>

      {/* Info */}
      <div className="flex gap-3">
        {/* Creator Avatar */}
        {creator ? (
          <div 
            className="w-9 h-9 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); navigate(`/c/${creator.slug}`); }}
          >
            {creator.profile_image_url ? (
              <img src={creator.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-bold text-purple-400">
                {creator.name?.charAt(0)}
              </div>
            )}
          </div>
        ) : (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex-shrink-0 flex items-center justify-center">
            <Play className="h-4 w-4 text-white" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="font-medium text-sm sm:text-base line-clamp-2 leading-tight mb-1 group-hover:text-white transition">
            {video.title}
          </h3>
          
          {/* Creator Name */}
          {creator && (
            <p 
              className="text-gray-400 text-xs sm:text-sm flex items-center gap-1 hover:text-gray-300"
              onClick={(e) => { e.stopPropagation(); navigate(`/c/${creator.slug}`); }}
            >
              {creator.name}
              {creator.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-gray-400" />}
            </p>
          )}
          
          {/* Views & Time */}
          <p className="text-gray-400 text-xs sm:text-sm">
            {formatViews(video.view_count || video.views)} 
            {video.created_at && ` \u00B7 ${formatTimeAgo(video.created_at)}`}
          </p>
        </div>
      </div>
    </div>
  );
};

// ===== CREATOR CARD COMPONENT (for horizontal scroll) =====
const CreatorCard = ({ creator, navigate }) => {
  return (
    <div
      onClick={() => navigate(`/c/${creator.slug}`)}
      className="flex-shrink-0 w-32 sm:w-36 cursor-pointer group text-center"
    >
      <div className="relative mb-2">
        <div className="w-24 h-24 sm:w-28 sm:h-28 mx-auto rounded-full overflow-hidden bg-gray-800 border-2 border-transparent group-hover:border-purple-500 transition-all duration-300">
          {creator.profile_image_url ? (
            <img 
              src={creator.profile_image_url} 
              alt={creator.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-purple-400 bg-gradient-to-br from-purple-900/50 to-pink-900/50">
              {creator.name?.charAt(0) || 'C'}
            </div>
          )}
        </div>
        {creator.is_verified && (
          <div className="absolute bottom-0 right-1/4 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center border-2 border-[#0f0f0f]">
            <BadgeCheck className="w-3.5 h-3.5 text-white" />
          </div>
        )}
      </div>
      <h3 className="font-medium text-sm truncate group-hover:text-purple-400 transition-colors">
        {creator.name}
      </h3>
      <p className="text-xs text-gray-400">
        {creator.subscriber_count?.toLocaleString() || 0} subscribers
      </p>
    </div>
  );
};

// ===== CREATOR PROFILE CARD (for grid layout - same style as VideoCard) =====
const CreatorProfileCard = ({ creator, navigate }) => {
  return (
    <div
      onClick={() => navigate(`/c/${creator.slug}`)}
      className="cursor-pointer group"
      data-testid={`creator-profile-card-${creator.id}`}
    >
      {/* Banner/Cover Image - Same aspect ratio as video thumbnails */}
      <div className="relative aspect-video rounded-xl overflow-hidden bg-[#1a1a1a] mb-3">
        {creator.banner_image_url ? (
          <img 
            src={creator.banner_image_url} 
            alt={creator.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 via-pink-900/30 to-indigo-900/50">
            {/* Large profile picture in center if no banner */}
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-800 border-4 border-purple-500/30">
              {creator.profile_image_url ? (
                <img 
                  src={creator.profile_image_url} 
                  alt={creator.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-purple-400">
                  {creator.name?.charAt(0) || 'C'}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Profile picture overlay (bottom left) */}
        {creator.banner_image_url && (
          <div className="absolute bottom-2 left-2 w-10 h-10 rounded-full overflow-hidden bg-gray-800 border-2 border-white/20">
            {creator.profile_image_url ? (
              <img 
                src={creator.profile_image_url} 
                alt={creator.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-bold text-purple-400 bg-purple-900/50">
                {creator.name?.charAt(0) || 'C'}
              </div>
            )}
          </div>
        )}

        {/* Verified Badge */}
        {creator.is_verified && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-purple-600/90 backdrop-blur-sm rounded-full flex items-center gap-1">
            <BadgeCheck className="w-3.5 h-3.5 text-white" />
            <span className="text-xs text-white font-medium">Verified</span>
          </div>
        )}
      </div>
      
      {/* Creator Info - Below thumbnail like VideoCard */}
      <div className="flex gap-3">
        {/* Avatar (only shown if no banner, otherwise already shown in overlay) */}
        {!creator.banner_image_url && (
          <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
            {creator.profile_image_url ? (
              <img src={creator.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-bold text-purple-400">
                {creator.name?.charAt(0)}
              </div>
            )}
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          {/* Creator Name */}
          <h3 className="font-medium text-sm sm:text-base text-white group-hover:text-purple-400 transition-colors line-clamp-1 flex items-center gap-1">
            {creator.name}
            {creator.is_verified && !creator.banner_image_url && (
              <BadgeCheck className="w-4 h-4 text-purple-400 flex-shrink-0" />
            )}
          </h3>
          
          {/* Subscriber count */}
          <p className="text-gray-400 text-xs sm:text-sm">
            {(creator.subscriber_count || 0).toLocaleString()} subscribers
          </p>
          
          {/* Bio snippet if available */}
          {creator.bio && (
            <p className="text-gray-500 text-xs mt-1 line-clamp-1">
              {creator.bio}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== FEATURED VIDEO CARD - YouTube-style hero video =====
const FeaturedVideoCard = ({ video, navigate, creators }) => {
  const creator = creators?.find(c => c.id === video.creator_id);
  
  return (
    <div 
      className="group cursor-pointer lg:flex lg:gap-6"
      onClick={() => navigate(`/vod/video/${video.slug || video.id}?autoplay=true`)}
      data-testid="featured-video-card"
    >
      {/* Thumbnail - Larger on desktop */}
      <div className="relative aspect-video lg:aspect-[16/9] lg:flex-shrink-0 lg:w-[60%] xl:w-[55%] rounded-xl overflow-hidden bg-[#1a1a1a] mb-3 lg:mb-0">
        {video.thumbnail_url ? (
          <img 
            src={video.thumbnail_url} 
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/30 to-pink-900/30">
            <Play className="h-16 w-16 text-gray-500" />
          </div>
        )}
        
        {/* Duration Badge */}
        {video.duration > 0 && (
          <span className="absolute bottom-3 right-3 bg-black/90 text-white text-sm font-medium px-2 py-1 rounded">
            {formatDuration(video.duration)}
          </span>
        )}
        
        {/* Premium Badge */}
        {video.tier === 'premium' && (
          <span className="absolute top-3 left-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-2.5 py-1 rounded flex items-center gap-1">
            <Crown className="h-3.5 w-3.5" /> PREMIUM
          </span>
        )}

        {/* LIVE Badge */}
        {video.is_live && (
          <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded flex items-center gap-1.5">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> LIVE
          </span>
        )}

        {/* Hover Play Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="w-16 h-16 rounded-full bg-black/70 flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform">
            <Play className="h-8 w-8 text-white fill-white ml-1" />
          </div>
        </div>
      </div>

      {/* Info Section - Side-by-side on desktop */}
      <div className="lg:flex-1 lg:py-2">
        <div className="flex gap-3 lg:flex-col lg:gap-0">
          {/* Creator Avatar - Mobile only inline */}
          <div className="lg:hidden">
            {creator ? (
              <div 
                className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 cursor-pointer"
                onClick={(e) => { e.stopPropagation(); navigate(`/c/${creator.slug}`); }}
              >
                {creator.profile_image_url ? (
                  <img src={creator.profile_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm font-bold text-purple-400">
                    {creator.name?.charAt(0)}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex-shrink-0 flex items-center justify-center">
                <Play className="h-4 w-4 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Title */}
            <h2 className="font-semibold text-base sm:text-lg lg:text-xl line-clamp-2 leading-tight mb-2 group-hover:text-white transition">
              {video.title}
            </h2>
            
            {/* Creator info - Desktop inline */}
            <div className="hidden lg:flex items-center gap-3 mb-3">
              {creator && (
                <>
                  <div 
                    className="w-9 h-9 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigate(`/c/${creator.slug}`); }}
                  >
                    {creator.profile_image_url ? (
                      <img src={creator.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-sm font-bold text-purple-400 bg-gray-800">
                        {creator.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <p 
                    className="text-gray-300 text-sm flex items-center gap-1 hover:text-white cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigate(`/c/${creator.slug}`); }}
                  >
                    {creator.name}
                    {creator.is_verified && <BadgeCheck className="h-4 w-4 text-gray-400" />}
                  </p>
                </>
              )}
            </div>
            
            {/* Creator Name - Mobile */}
            {creator && (
              <p 
                className="lg:hidden text-gray-400 text-sm flex items-center gap-1 hover:text-gray-300 mb-1"
                onClick={(e) => { e.stopPropagation(); navigate(`/c/${creator.slug}`); }}
              >
                {creator.name}
                {creator.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-gray-400" />}
              </p>
            )}
            
            {/* Views & Time */}
            <p className="text-gray-400 text-sm">
              {formatViews(video.view_count || video.views)} 
              {video.created_at && ` · ${formatTimeAgo(video.created_at)}`}
            </p>

            {/* Description - Desktop only */}
            {video.description && (
              <p className="hidden lg:block text-gray-400 text-sm mt-3 line-clamp-2">
                {video.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VODLanding;
